import React, { useState } from "react";
import { TabContent, TabPane } from "reactstrap";
import PatientListSection from "./PatientListSection";
import { FAILED_VOICEMAIL_NOTE_DESCRIPTION } from "./monthlyPhoneConsultationLabels";

const PatientOutreachReport = ({
  haveMet,
  haveNotMet,
  filters,
  programType,
  mpcCopy,
  onViewPatient,
  onViewNotes,
  onViewFailedAttempts,
  onViewSuccessfulConsultations
}) => {
  const [activeTab, setActiveTab] = useState("haveMet");
  const haveMetCount = haveMet.length;
  const haveNotMetCount = haveNotMet.length;
  const totalPatientsCount = haveMetCount + haveNotMetCount;

  return (
    <div className="mpc-report-panel mpc-report-panel--patient" role="tabpanel" id="mpc-report-panel-patientOutreach">
      <div className="mpc-tabs-shell">
        <div className="mpc-tab-bar" role="tablist" aria-label="Patient consultation status">
          <div className="mpc-tab-bar-tabs">
            <button
              type="button"
              role="tab"
              id="mpc-tab-have-met"
              aria-controls="mpc-panel-have-met"
              aria-selected={activeTab === "haveMet"}
              className={`mpc-tab mpc-tab--met ${activeTab === "haveMet" ? "active" : ""}`}
              onClick={() => setActiveTab("haveMet")}
            >
              <span className="mpc-tab-icon success" aria-hidden="true">
                <i className="mdi mdi-phone-check-outline" />
              </span>
              <span className="mpc-tab-text">
                <span className="mpc-tab-label">Have Met</span>
                <span className="mpc-tab-sublabel">{mpcCopy.haveMetTabSublabel}</span>
              </span>
              <span className="mpc-tab-count">{haveMetCount}</span>
            </button>
            <button
              type="button"
              role="tab"
              id="mpc-tab-have-not-met"
              aria-controls="mpc-panel-have-not-met"
              aria-selected={activeTab === "haveNotMet"}
              className={`mpc-tab mpc-tab--not-met ${activeTab === "haveNotMet" ? "active" : ""}`}
              onClick={() => setActiveTab("haveNotMet")}
            >
              <span className="mpc-tab-icon warning" aria-hidden="true">
                <i className="mdi mdi-phone-alert-outline" />
              </span>
              <span className="mpc-tab-text">
                <span className="mpc-tab-label">Have Not Met</span>
                <span className="mpc-tab-sublabel">Needs outreach</span>
              </span>
              <span className="mpc-tab-count">{haveNotMetCount}</span>
            </button>
          </div>
          <div className="mpc-tab-bar-total" aria-label={`Total patients: ${totalPatientsCount}`}>
            <span className="mpc-tab-bar-total-label">Total Patient:</span>
            <span className="mpc-tab-bar-total-value">{totalPatientsCount}</span>
          </div>
        </div>

        <div className="mpc-tab-panel">
          <TabContent activeTab={activeTab} className="mpc-tab-content">
            <TabPane tabId="haveMet" role="tabpanel" id="mpc-panel-have-met" aria-labelledby="mpc-tab-have-met">
              {activeTab === "haveMet" && (
                <PatientListSection
                  title="Have Met"
                  description={mpcCopy.haveMetDescription}
                  patients={haveMet}
                  mpcCopy={mpcCopy}
                  showNotesColumn
                  showFailedAttemptsColumn
                  showAverageAttemptsBeforeConsultationColumn
                  showCallAttemptsColumn
                  listIcon="mdi-phone-check-outline"
                  fileSuffix={`monthly-phone-consultation-${programType}-met-${filters.year}-${filters.month}`}
                  onViewPatient={onViewPatient}
                  onViewNotes={onViewNotes}
                  onViewFailedAttempts={onViewFailedAttempts}
                  onViewSuccessfulConsultations={onViewSuccessfulConsultations}
                  embedded
                />
              )}
            </TabPane>
            <TabPane tabId="haveNotMet" role="tabpanel" id="mpc-panel-have-not-met" aria-labelledby="mpc-tab-have-not-met">
              {activeTab === "haveNotMet" && (
                <PatientListSection
                  title="Have Not Met"
                  description={mpcCopy.haveNotMetDescription}
                  patients={haveNotMet}
                  mpcCopy={mpcCopy}
                  showNotesColumn={false}
                  showFailedAttemptsColumn
                  listIcon="mdi-phone-alert-outline"
                  fileSuffix={`monthly-phone-consultation-${programType}-not-met-${filters.year}-${filters.month}`}
                  onViewPatient={onViewPatient}
                  onViewFailedAttempts={onViewFailedAttempts}
                  embedded
                />
              )}
            </TabPane>
          </TabContent>
        </div>
      </div>

      <div className="mpc-help-card">
        <h6>
          <i className="mdi mdi-book-open-variant" />
          Report Legend
        </h6>
        <div className="mpc-help-grid">
          <div className="mpc-help-item">
            <span className="mpc-help-label">What it shows</span>
            <strong>
              Patient-level monthly phone consultation status, documented consultations, failed outreach attempts,
              and consultation success rates for the selected month.
            </strong>
          </div>
          <div className="mpc-help-item">
            <span className="mpc-help-label">Have Met</span>
            <strong>
              <span className="mpc-help-tag met">Have Met</span> The patient has a documented{" "}
              <em>{mpcCopy.consultationTitle}</em> during the selected month.
            </strong>
          </div>
          <div className="mpc-help-item">
            <span className="mpc-help-label">Have Not Met</span>
            <strong>
              <span className="mpc-help-tag not-met">Have Not Met</span> No{" "}
              <em>{mpcCopy.consultationTitle}</em> was documented for the selected month.{" "}
              <em>{FAILED_VOICEMAIL_NOTE_DESCRIPTION}</em> are still displayed to show outreach attempts.
            </strong>
          </div>
          <div className="mpc-help-item">
            <span className="mpc-help-label">Failed attempts</span>
            <strong>
              Counted from patient notes with subject <em>Monthly Phone Call - Left Voicemail</em> or legacy body{" "}
              <em>Left Voicemail</em>. Color scale: 1-2 = yellow, 3+ = red.
            </strong>
          </div>
          <div className="mpc-help-item">
            <span className="mpc-help-label">Table columns</span>
            <strong>
              <em>Total Consultations Notes</em> is the total outreach count (documented consultations plus failed voicemail attempts).{" "}
              <em>Failed Phone Attempts This Month</em> opens voicemail notes only.{" "}
              <em>Calls to Consultation</em> shows successful consultation count for Have Met patients.{" "}
              <em>Average Consultation Success Rate</em> is based on failed + successful attempts (100% = no failed
              attempts).
            </strong>
          </div>
          <div className="mpc-help-item">
            <span className="mpc-help-label">Suggested workflow</span>
            <strong>
              Review Have Not Met patients first, open failed-attempt notes, then follow up on Have Met patients with
              high attempt counts.
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientOutreachReport;
